import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

// organism — DWR builder/level-up right rail. Header con counter "Avanzamento N/M"
// + lista di sezioni con stato done/todo + evidenza "current" via scroll-spy.
// Pattern .aside .rail-list / .rail-header / .rail-counter / .rail-item mockup
// (dev-workspace/mockups/.../project/styles.css righe 887-953).
// Click su rail-item → onJump(id): in PR 3 il consumer fa scrollIntoView sulla sezione
// (decisione A1 del WIP). Rende un <div>: il container <aside> esterno è gestito dal
// template <Aside> del consumer (stesso pattern di <LevelsRail> in CharacterViewPage).

export type StepRailStatus = 'done' | 'todo';

export interface StepRailSection {
  id: string;
  label: string;
  stepNumber: string;
}

interface StepRailProps {
  sections: readonly StepRailSection[];
  currentId: string | null;
  getStatus: (id: string) => StepRailStatus;
  onJump: (id: string) => void;
  className?: string;
}

export function StepRail({ sections, currentId, getStatus, onJump, className }: StepRailProps) {
  const total = sections.length;
  const completed = sections.filter((s) => getStatus(s.id) === 'done').length;

  return (
    <div
      // Container trasparente: eredita il bg paper-soft del template <Aside>
      // parent. Le rail-item current/hover diventano paper-bright per dare
      // contrasto evidente vs lo sfondo sand-color dell'aside (feedback PR 4).
      className={cn('border-rule border bg-transparent px-[0.9rem] py-[1rem]', className)}
      aria-label="Avanzamento sezioni creazione personaggio"
    >
      <div className="border-ink mb-[0.6rem] flex items-baseline justify-between border-b-[1.5px] px-[0.25rem] pb-[0.6rem]">
        <span className="font-display text-ink-soft text-[0.7rem] font-bold tracking-[0.08em] uppercase">
          Avanzamento
        </span>
        <span className="font-display text-[1.05rem] font-bold">
          {completed}
          <span className="font-normal opacity-50"> / {total}</span>
        </span>
      </div>

      <ol className="m-0 flex list-none flex-col gap-px p-0">
        {sections.map((s) => {
          const status = getStatus(s.id);
          const isCurrent = s.id === currentId;
          return (
            <li key={s.id} className="list-none">
              <RailItem
                section={s}
                status={status}
                isCurrent={isCurrent}
                onJump={() => onJump(s.id)}
              />
            </li>
          );
        })}
      </ol>
    </div>
  );
}

interface RailItemProps {
  section: StepRailSection;
  status: StepRailStatus;
  isCurrent: boolean;
  onJump: () => void;
}

function RailItem({ section, status, isCurrent, onJump }: RailItemProps) {
  const isDone = status === 'done';
  const textColor = isDone || isCurrent ? 'text-ink' : 'text-ink-faint';
  const borderLeft = isCurrent ? 'border-l-accent-dwr' : 'border-l-transparent';
  const bg = isCurrent ? 'bg-paper-bright' : 'hover:bg-paper-bright bg-transparent';
  const iconClasses = railIconClasses(isDone, isCurrent);

  return (
    <button
      type="button"
      onClick={onJump}
      data-status={status}
      data-current={isCurrent ? 'true' : undefined}
      aria-current={isCurrent ? 'step' : undefined}
      className={cn(
        'flex w-full items-center gap-[0.7rem] border-0 border-l-2 px-[0.45em] py-[0.55em] text-left transition-[background-color,border-color] duration-[120ms]',
        textColor,
        borderLeft,
        bg,
      )}
    >
      <span
        aria-hidden
        className={cn(
          'grid h-[22px] w-[22px] flex-shrink-0 place-items-center border-[1.5px]',
          iconClasses,
        )}
      >
        {isDone ? (
          <Check className="h-[14px] w-[14px]" strokeWidth={2.4} />
        ) : (
          <span className="font-display text-[0.62rem] opacity-50">{section.stepNumber}</span>
        )}
      </span>
      <span className="font-display text-[0.82rem] tracking-[0.07em] uppercase">
        {section.label}
      </span>
    </button>
  );
}

function railIconClasses(isDone: boolean, isCurrent: boolean): string {
  if (isDone) return 'bg-ink text-paper-bright border-ink';
  if (isCurrent) return 'border-accent-dwr text-accent-dwr';
  return 'border-current';
}
